import { useQuery } from "@tanstack/react-query";
import { checkinApi } from "@bw/shared/api";
import type { CheckinPassResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * Client refetch cadence (~60s). The server token TTL is 5 minutes, so a fresh
 * token is always fetched well before the displayed one expires.
 */
export const BOOKING_PASS_REFRESH_MS = 60_000;

/**
 * The short-lived QR check-in pass for a confirmed booking (GET /checkin/
 * bookings/{id}/pass). The host OR any accepted guest on the roster may mint it
 * — NOT host-only.
 *
 * `enabled` gates the fetch on the dialog being open, so the pass is only minted
 * when the player actually opens the QR (no background pass on a closed dialog).
 * While open it auto-refetches every ~60s and the returned `refetch` drives the
 * manual refresh — the displayed token never goes stale. The httpClient rejects
 * on 404/403/409 (never a fabricated pass), so the dialog can surface an honest
 * error with NO QR rendered. `gcTime`/`staleTime` 0 so a stale token is never
 * served from cache.
 */
export function useBookingPass(
  bookingId: string | undefined,
  enabled: boolean,
) {
  return useQuery<CheckinPassResponse>({
    queryKey: queryKeys.booking.pass(bookingId ?? ""),
    queryFn: () => checkinApi.getBookingPass(bookingId as string),
    enabled: enabled && Boolean(bookingId),
    refetchInterval: enabled ? BOOKING_PASS_REFRESH_MS : false,
    gcTime: 0,
    staleTime: 0,
  });
}
